<template>
  <div class="annotation-field"
    :data-annotation-id="annotation.id"
    :class="[`field-${annotation.type}`, {
    'field-required': annotation.required,
    'field-selected': selected,
    'field-editing': editing,
    'field-dragging': dragging
  }]" :style="positionStyle" @click="handleClick" @mousedown="handleMouseDown">
    <div class="field-label" v-if="showLabel && annotation.label">
      {{ annotation.label }}
      <span v-if="annotation.required" class="required-marker">*</span>
    </div>
    <div class="field-content">
      <slot></slot>
    </div>
    <div v-if="selected && editable" class="field-handles">
      <!-- Corner handles -->
      <div class="resize-handle resize-handle-nw" @mousedown.stop="startResize('nw', $event)"></div>
      <div class="resize-handle resize-handle-ne" @mousedown.stop="startResize('ne', $event)"></div>
      <div class="resize-handle resize-handle-se" @mousedown.stop="startResize('se', $event)"></div>
      <div class="resize-handle resize-handle-sw" @mousedown.stop="startResize('sw', $event)"></div>
      <!-- Midpoint handles -->
      <div class="resize-handle resize-handle-n" @mousedown.stop="startResize('n', $event)"></div>
      <div class="resize-handle resize-handle-e" @mousedown.stop="startResize('e', $event)"></div>
      <div class="resize-handle resize-handle-s" @mousedown.stop="startResize('s', $event)"></div>
      <div class="resize-handle resize-handle-w" @mousedown.stop="startResize('w', $event)"></div>
    </div>
    <!-- Dimension label for selected field -->
    <div v-if="selected && editable" class="dimension-label">
      {{ Math.round(annotation.position.width) }} x {{ Math.round(annotation.position.height) }}
    </div>
  </div>
</template>

<script>
import Vue from 'vue';

export default {
  name: 'BaseField',
  props: {
    annotation: {
      type: Object,
      required: true
    },
    showLabel: {
      type: Boolean,
      default: false
    },
    scale: {
      type: Number,
      default: 1
    },
    selected: {
      type: Boolean,
      default: false
    },
    editing: {
      type: Boolean,
      default: false
    },
    editable: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      dragging: false,
      resizing: false,
      dragStartX: 0,
      dragStartY: 0,
      originalPosition: null,
      resizeHandle: '',
      resizeStartX: 0,
      resizeStartY: 0,
      originalSize: null
    };
  },
  computed: {
    positionStyle() {
      const { x, y, width, height } = this.annotation.position;
      return {
        position: 'absolute',
        left: `${x * this.scale}px`,
        top: `${y * this.scale}px`,
        width: `${width * this.scale}px`,
        height: `${height * this.scale}px`
      };
    }
  },
  methods: {
    handleClick() {
      console.log('[BaseField] handleClick - emitting field-click with:', this.annotation);
      this.$emit('field-click', this.annotation);
    },

    cleanupEventListeners() {
      // Remove any existing event listeners
      document.removeEventListener('mousemove', this.handleMouseMove);
      document.removeEventListener('mouseup', this.handleMouseUp);
    },

    handleMouseDown(event) {
      if (!this.editable || this.resizing) return;

      // Clean up any existing listeners first (defensive programming)
      this.cleanupEventListeners();

      this.dragging = true;
      this.dragStartX = event.clientX;
      this.dragStartY = event.clientY;
      this.originalPosition = { ...this.annotation.position };

      // Add event listeners
      document.addEventListener('mousemove', this.handleMouseMove);
      document.addEventListener('mouseup', this.handleMouseUp);

      event.preventDefault();
    },

    handleMouseMove(event) {
      if (this.dragging) {
        const deltaX = (event.clientX - this.dragStartX) / this.scale;
        const deltaY = (event.clientY - this.dragStartY) / this.scale;

        const newPosition = {
          x: this.originalPosition.x + deltaX,
          y: this.originalPosition.y + deltaY,
          width: this.originalPosition.width,
          height: this.originalPosition.height
        };

        this.$emit('position-change', { id: this.annotation.id, position: newPosition });
      } else if (this.resizing) {
        this.handleResize(event);
      }
    },

    handleMouseUp(event) {
      // Prevent event from bubbling up
      if (event) {
        event.stopPropagation();
      }

      // Reset state flags
      this.dragging = false;
      this.resizing = false;

      // Clean up event listeners using the centralized cleanup method
      this.cleanupEventListeners();
    },

    startResize(handle, event) {
      // Clean up any existing listeners first
      this.cleanupEventListeners();

      this.resizing = true;
      this.resizeHandle = handle;
      this.resizeStartX = event.clientX;
      this.resizeStartY = event.clientY;
      this.originalSize = { ...this.annotation.position };

      document.addEventListener('mousemove', this.handleMouseMove);
      document.addEventListener('mouseup', this.handleMouseUp);

      event.preventDefault();
      event.stopPropagation();
    },

    handleResize(event) {
      const deltaX = (event.clientX - this.resizeStartX) / this.scale;
      const deltaY = (event.clientY - this.resizeStartY) / this.scale;

      let newPosition = { ...this.originalSize };

      switch (this.resizeHandle) {
        // Corner handles
        case 'ne':
          newPosition.width += deltaX;
          newPosition.y += deltaY;
          newPosition.height -= deltaY;
          break;
        case 'se':
          newPosition.width += deltaX;
          newPosition.height += deltaY;
          break;
        case 'sw':
          newPosition.x += deltaX;
          newPosition.width -= deltaX;
          newPosition.height += deltaY;
          break;
        case 'nw':
          newPosition.x += deltaX;
          newPosition.width -= deltaX;
          newPosition.y += deltaY;
          newPosition.height -= deltaY;
          break;
        // Midpoint handles (single-axis resizing)
        case 'n':
          newPosition.y += deltaY;
          newPosition.height -= deltaY;
          break;
        case 'e':
          newPosition.width += deltaX;
          break;
        case 's':
          newPosition.height += deltaY;
          break;
        case 'w':
          newPosition.x += deltaX;
          newPosition.width -= deltaX;
          break;
      }

      // Ensure minimum size
      newPosition.width = Math.max(15, newPosition.width);
      newPosition.height = Math.max(15, newPosition.height);

      this.$emit('position-change', { id: this.annotation.id, position: newPosition });
    }
  },
  beforeDestroy() {
    // Clean up any active event listeners to prevent memory leaks
    // This is crucial if the component is destroyed while dragging or resizing
    this.cleanupEventListeners();

    // Reset state flags
    this.dragging = false;
    this.resizing = false;
  }
};
</script>

<style scoped>
/* DriveScout Field Colors - Clean Admin Theme */
.annotation-field {
  /* DriveScout color palette */
  --field-signature-color: #2B83FC;
  --field-text-color: #64BC5F;
  --field-select-color: #9090ce;
  --field-date-color: #F9C932;
  --selection-color: #2B83FC;

  border: 2px solid transparent;
  background-color: rgba(255, 255, 255, 0.95);
  cursor: pointer;
  user-select: none;
  border-radius: 3px;
  /* Subtle inner shadow to improve visibility against various PDF backgrounds */
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.05);
}

/* Field type specific styling */
.annotation-field.field-signature {
  --current-field-color: var(--field-signature-color);
  background-color: rgba(43, 131, 252, 0.10);
  border-color: var(--field-signature-color);
}

.annotation-field.field-text {
  --current-field-color: var(--field-text-color);
  background-color: rgba(100, 188, 95, 0.10);
  border-color: var(--field-text-color);
}

.annotation-field.field-select {
  --current-field-color: var(--field-select-color);
  background-color: rgba(144, 144, 206, 0.10);
  border-color: var(--field-select-color);
}

.annotation-field.field-date {
  --current-field-color: var(--field-date-color);
  background-color: rgba(249, 201, 50, 0.12);
  border-color: var(--field-date-color);
}

/* Hover state - darkened border, move cursor */
.annotation-field:hover:not(.field-selected):not(.field-dragging) {
  border-color: var(--current-field-color);
  filter: brightness(0.95);
  cursor: move;
}

/* Selected state - blue border, handles visible */
.annotation-field.field-selected {
  border-width: 2px;
  border-color: var(--selection-color);
  box-shadow: 0 0 0 3px rgba(43, 131, 252, 0.15);
  z-index: 100;
}

/* Dragging state */
.annotation-field.field-dragging {
  opacity: 0.9;
  cursor: grabbing;
  z-index: 200;
}

.field-label {
  font-size: 12px;
  font-weight: 600;
  padding: 4px 8px;
  background-color: rgba(0, 0, 0, 0.05);
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.required-marker {
  color: #e53e3e;
  margin-left: 2px;
}

.field-content {
  position: relative;
  width: 100%;
  height: 100%;
  padding: 0px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Resize handles container */
.field-handles {
  position: absolute;
  top: -7px;
  left: -7px;
  right: -7px;
  bottom: -7px;
  pointer-events: none;
}

/* Base resize handle - 14px square */
.resize-handle {
  position: absolute;
  width: 14px;
  height: 14px;
  background-color: #fff;
  border: 2px solid var(--selection-color);
  border-radius: 2px;
  pointer-events: auto;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

/* Corner handles */
.resize-handle-nw {
  top: 0;
  left: 0;
  cursor: nw-resize;
}

.resize-handle-ne {
  top: 0;
  right: 0;
  cursor: ne-resize;
}

.resize-handle-se {
  bottom: 0;
  right: 0;
  cursor: se-resize;
}

.resize-handle-sw {
  bottom: 0;
  left: 0;
  cursor: sw-resize;
}

/* Midpoint handles - positioned at edge centers */
.resize-handle-n {
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  cursor: n-resize;
}

.resize-handle-e {
  top: 50%;
  right: 0;
  transform: translateY(-50%);
  cursor: e-resize;
}

.resize-handle-s {
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  cursor: s-resize;
}

.resize-handle-w {
  top: 50%;
  left: 0;
  transform: translateY(-50%);
  cursor: w-resize;
}

/* Dimension label - shows width x height */
.dimension-label {
  position: absolute;
  bottom: -24px;
  left: 50%;
  transform: translateX(-50%);
  background-color: #2d3748;
  color: #fff;
  font-family: 'SF Mono', 'Monaco', 'Consolas', monospace;
  font-size: 11px;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 3px;
  white-space: nowrap;
  z-index: 101;
  pointer-events: none;
}

/* Mobile responsive adjustments */
@media (max-width: 768px) {
  .annotation-field {
    min-width: 44px;
    min-height: 44px;
  }

  .resize-handle {
    width: 18px;
    height: 18px;
  }

  .field-label {
    font-size: 13px;
    padding: 6px 10px;
  }
}

/* Accessibility: High contrast mode */
@media (prefers-contrast: high) {
  .annotation-field {
    border-width: 3px;
  }

  .annotation-field.field-selected {
    border-width: 3px;
  }

  .resize-handle {
    border-width: 3px;
  }
}

/* Focus state for keyboard navigation */
.annotation-field:focus-visible {
  outline: 3px solid var(--selection-color);
  outline-offset: 2px;
}

/* Print styles */
@media print {
  .annotation-field {
    box-shadow: none;
    border: 1px solid #000;
  }

  .field-handles,
  .dimension-label {
    display: none;
  }
}
</style>
